import React, { useEffect, useState } from "react";
import { Modal, Button, Tooltip } from "antd";
import { LeftOutlined, RightOutlined, DownloadOutlined } from "@ant-design/icons";
import DualcamPlayer from "./DualcamPlayer";
import { triggerDownload } from "./api";
import type { MediaEntry } from "./types";

const navButtonStyle = (side: "left" | "right"): React.CSSProperties => ({
    position: "absolute",
    top: "50%",
    [side]: 8,
    transform: "translateY(-50%)",
    zIndex: 10,
    background: "rgba(0,0,0,0.45)",
    color: "#fff",
    border: "none",
});

const downloadButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: 8,
    left: 8,
    zIndex: 10,
    background: "rgba(0,0,0,0.45)",
    color: "#fff",
    border: "none",
};

/** Player modal shared by the gallery and map pages — shows one MediaEntry from a list, with
 * prev/next via on-screen buttons and left/right arrow keys, matching the "handy-app"-style
 * next/prev navigation the Android app's swipe-to-next/prev does for the same list of items. */
const DualcamPlayerModal: React.FC<{
    items: MediaEntry[];
    index: number | null;
    onIndexChange: (index: number | null) => void;
    // Both optional and only supplied by the album page: the map page renders this same modal without
    // them, and download simply isn't offered there rather than needing its own token/albumId plumbing.
    token?: string;
    albumId?: number;
}> = ({ items, index, onIndexChange, token, albumId }) => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const [downloading, setDownloading] = useState(false);
    const open = index !== null;
    const hasPrev = index !== null && index > 0;
    const hasNext = index !== null && index < items.length - 1;

    const goPrev = () => { if (index !== null && index > 0) onIndexChange(index - 1); };
    const goNext = () => { if (index !== null && index < items.length - 1) onIndexChange(index + 1); };

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof Element && e.target.closest("input, button, video, audio, [role=slider]")) return;
            if (e.key === "ArrowLeft") goPrev();
            else if (e.key === "ArrowRight") goNext();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index, items.length]);

    const entry = index !== null ? items[index] : null;
    const canDownload = token != null && albumId != null;


    return (
        <Modal
            wrapClassName="sf-player-modal"
            open={open}
            onCancel={() => onIndexChange(null)}
            title={isGerman ? "Medienanzeige" : "Media viewer"}
            footer={null}
            width="min(960px, 96vw)"
            destroyOnHidden
            centered
        >
            {entry && (
                <div style={{ position: "relative" }}>
                    {canDownload && (
                        // No way to know up front whether this album's Synology share allows downloads
                        // (see api.ts's triggerDownload) — the tooltip says so instead of the button
                        // silently disappearing or a failed attempt looking like a bug.
                        <Tooltip title="Download — can fail if the album owner has disabled downloads for this share">
                            <Button
                                shape="circle"
                                aria-label={isGerman ? "Medien herunterladen" : "Download media"}
                                icon={<DownloadOutlined />}
                                loading={downloading}
                                onClick={async () => {
                                    setDownloading(true);
                                    await triggerDownload(token!, albumId!, [entry]);
                                    setDownloading(false);
                                }}
                                style={downloadButtonStyle}
                            />
                        </Tooltip>
                    )}
                    {hasPrev && (
                        <Button shape="circle" aria-label={isGerman ? "Vorheriges Medium" : "Previous media"} icon={<LeftOutlined />} onClick={goPrev} style={navButtonStyle("left")} />
                    )}
                    <DualcamPlayer entry={entry} key={entry.id} />
                    {hasNext && (
                        <Button shape="circle" aria-label={isGerman ? "Nächstes Medium" : "Next media"} icon={<RightOutlined />} onClick={goNext} style={navButtonStyle("right")} />
                    )}
                </div>
            )}
        </Modal>
    );
};

export default DualcamPlayerModal;
